import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import {
  ActivityInterface,
  BaseUserInterface,
  GetConsentInterface,
  PaginatedResponseInterface,
  ReviewInterface,
  TimeslotFilterParamsInterface,
  TimeslotInterface,
} from '@sport-activities/sdk';
import { EMPTY, Observable } from 'rxjs';
import { concatMap, expand, map, reduce } from 'rxjs/operators';
import { ActivityFilterParamsInterface } from '~/attendee/interfaces/activity-filter-params.interface';

import { ACTIVITIES_BASE_URL } from '~/core/core.module';
import { createHttpParams } from '~/core/helpers/create-http-params/create-http-params.helper';
import { StatfulReviewFiltersInterface } from '~/core/interfaces/statful-review-filters.interface';
import { Logger } from '~/core/logger.service';
import { Activity } from '~/core/model/activity.model';
import { ReviewStat } from '~/core/model/review-stats.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { ParamethorService } from '~/paramethor/paramethor.service';

const DEFAULT_LIMIT = 100;

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  constructor(
    @Inject(ACTIVITIES_BASE_URL) protected readonly activitiesUrl: string,
    protected readonly httpClient: HttpClient,
    protected readonly localizeRouterService: LocalizeRouterService,
    protected readonly logger: Logger,
    protected readonly paramethorService: ParamethorService
  ) {}

  /** get activities */
  public getActivities(
    filters: ActivityFilterParamsInterface
  ): Observable<PaginatedResponseInterface<Activity>> {
    return this.httpClient
      .get<PaginatedResponseInterface<ActivityInterface>>(
        `${this.activitiesUrl}/activities`,
        {
          params: createHttpParams(filters),
        }
      )
      .pipe(
        map((response: PaginatedResponseInterface<ActivityInterface>) => {
          return {
            total: response.total,
            items: response.items.map((activity) => new Activity(activity)),
          };
        })
      );
  }

  /** get activity by id */
  public getActivity(identifier: string): Observable<Activity> {
    return this.httpClient
      .get<ActivityInterface>(`${this.activitiesUrl}/activities/${identifier}`)
      .pipe(map((activityData) => new Activity(activityData)));
  }

  /** get the next available timeslot by activity id */
  public getActivityNextTimeslot(identifier: number): Observable<Timeslot> {
    return this.httpClient
      .get<TimeslotInterface>(
        `${this.activitiesUrl}/activities/${identifier}/timeslots`,
        {
          params: {
            limit: '1',
            timeslotStatus: 'PUBLISHED',
            sortBy: 'startDate',
            sortOrder: 'asc',
            excludeFull: 'true',
          },
        }
      )
      .pipe(
        map((timeslots) => {
          return timeslots?.[0] && new Timeslot(timeslots?.[0]);
        })
      );
  }

  /** get reviews by activity */
  public getActivityReviews(
    identifier: number | string,
    filters?: StatfulReviewFiltersInterface
  ): Observable<ReviewInterface[]> {
    let params: HttpParams;

    if (filters) {
      params = createHttpParams(filters);
    }

    return this.httpClient.get<ReviewInterface[]>(
      `${this.activitiesUrl}/activities/${identifier}/reviews`,
      { params }
    );
  }

  /** get reviews stats by activity */
  public getActivityReviewsStats(
    identifier: number | string,
    filters?: StatfulReviewFiltersInterface
  ): Observable<ReviewStat> {
    let params: HttpParams;

    if (filters) {
      params = createHttpParams(filters);
    }

    return this.httpClient
      .get<ReviewStat>(
        `${this.activitiesUrl}/activities/${identifier}/reviews-stats`,
        { params }
      )
      .pipe(map((reviewStatsData) => new ReviewStat(reviewStatsData)));
  }

  /** get timeslots by activity id */
  public getActivityTimeslots(
    identifier: string,
    filters?: TimeslotFilterParamsInterface,
    page = 1,
    limit = DEFAULT_LIMIT
  ): Observable<Timeslot[]> {
    let params: HttpParams;

    if (filters) {
      filters = {
        ...filters,
        pagination: {
          from: (page - 1) * limit,
          limit,
        },
      };
      params = createHttpParams(filters);
    }
    return this.httpClient
      .get<
        TimeslotInterface[]
      >(`${this.activitiesUrl}/activities/${identifier}/timeslots`, { params })
      .pipe(
        map((timeslotData) => {
          if (!timeslotData || !Array.isArray(timeslotData)) {
            this.logger.error(
              `${this.activitiesUrl}/activities/${identifier}/timeslots`
            );

            return [];
          }
          return timeslotData.map((timeslot) => new Timeslot(timeslot));
        })
      );
  }

  public getActivityFounder(activityId: string): Observable<BaseUserInterface> {
    return this.httpClient.get<BaseUserInterface>(
      `${this.activitiesUrl}/activities/${activityId}/founder`
    );
  }

  public getAllActivityTimeslots(
    identifier: string,
    filters?: TimeslotFilterParamsInterface,
    page = 1,
    limit = DEFAULT_LIMIT
  ): Observable<Timeslot[]> {
    return this.getActivityTimeslots(identifier, filters, page, limit).pipe(
      expand((timeslots) => {
        if (timeslots?.length >= limit) {
          page = page + 1;
          return this.getActivityTimeslots(identifier, filters, page, limit);
        }

        return EMPTY;
      }),
      concatMap((timeslots) => {
        return timeslots;
      }),
      reduce((accumulator, value) => {
        accumulator.push(value);
        return accumulator;
      }, [])
    );
  }

  public getConsents(): Observable<GetConsentInterface> {
    return this.httpClient.get<GetConsentInterface>(
      `${this.activitiesUrl}/me/consents`
    );
  }

  // M = Model Interface
  // F = Filter Interface
  public getPaginatedResults<M, F>(
    path: string,
    filters: F,
    model: new (value: Partial<M>) => M
  ): Observable<PaginatedResponseInterface<M>> {
    return this.httpClient
      .get<PaginatedResponseInterface<M>>(`${this.activitiesUrl}/${path}`, {
        params: createHttpParams(filters),
      })
      .pipe(
        map((response: PaginatedResponseInterface<M>) => {
          return {
            total: response.total,
            items: response.items.map((item) => new model(item)),
          };
        })
      );
  }

  /** get timeslot by id */
  public getTimeslot(sku: string): Observable<Timeslot> {
    return this.httpClient
      .get<TimeslotInterface>(`${this.activitiesUrl}/timeslots/${sku}`)
      .pipe(map((timeslotData) => new Timeslot(timeslotData)));
  }

  public getTimeslots(
    filters: TimeslotFilterParamsInterface = {}
  ): Observable<PaginatedResponseInterface<Timeslot>> {
    return this.getPaginatedResults<Timeslot, TimeslotFilterParamsInterface>(
      'timeslots',
      filters,
      Timeslot
    );
  }

  public isDatesPage(url: string): boolean {
    return (
      url.includes(
        this.localizeRouterService
          .translateRoute('attendee.show-activity-dates')
          .toString()
      ) ||
      url.includes(
        this.localizeRouterService
          .translateRoute('attendee.show-live-activity-dates')
          .toString()
      )
    );
  }

  public putConsents(
    consents: GetConsentInterface
  ): Observable<GetConsentInterface> {
    return this.httpClient.put<GetConsentInterface>(
      `${this.activitiesUrl}/me/consents`,
      consents
    );
  }
}
